import {
    Button,
    Field,
    Input,
    Textarea,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Drawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    DrawerFooter,
    makeStyles,
    tokens,
    Spinner
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { NewContractBranch } from '../../../../models/Entities/Contracts/NewContractBranch';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import ProtocolSelectorComponent from '../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';

// component functions
import * as ComponentFunctions from './BranchForm.Functions';

////i18Next
import { useTranslation, Trans } from 'react-i18next';

interface FormValidation {
    success: boolean;
    errors: string[];
}

export const BranchFormComponent = (props: ComponentFunctions.Props) => {

    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const [branchName, setBranchName] = useState<string>();
    const [branchDescription, setBranchDescription] = useState<string>();
    const [selectedProtocols, setSelectedProtocols] = useState<Protocol[] | undefined>([]);

    //useTranslation()
    const [t] = useTranslation();

    useEffect(() => {
        if (props.Contract === undefined)
            props.GetContractById(props.ContractId);
    }, [props.ContractId]);

    useEffect(() => {
        props.ContractBranchId && props.GetContractBranchById(props.ContractBranchId);
    }, [props.Contract !== undefined && props.ContractBranchId]);


    useEffect(() => {
        setBranchName(props.Branch?.branchName);
        setBranchDescription(props.Branch?.branchDescription);
    }, [props.Branch]);

    useEffect(() => {
        if (!props.Branch?.protocolIdRelatedToBranch) {
            setSelectedProtocols([]);
            return;
        }

        const selectedProtocol = (props.Protocols || []).find(
            (protocol: Protocol) => protocol.id === props.Branch?.protocolIdRelatedToBranch
        );

        setSelectedProtocols(selectedProtocol ? [selectedProtocol] : []);
    }, [props.Branch?.protocolIdRelatedToBranch, props.Protocols]);

    // save control
    useEffect(() => {
        if (submittedForm && (props.isAddedSuccessfully || props.Branch?.isUpdatedSuccessfully)) {
            setSubmittedForm(false);
            ResetForm();
            props.dismissPanel();
        }
    }, [submittedForm, props.isAddedSuccessfully, props.Branch?.isUpdatedSuccessfully]);


    const ResetForm = () => {
        setSubmittedForm(false);
        setBranchName(undefined);
        setBranchDescription(undefined);
        setSelectedProtocols([]);
    };

    // Events
    const onChangeBranchNameTextFieldValue = (
        event: React.ChangeEvent<HTMLInputElement>,
        data: { value: string }
    ) => {
        setBranchName(data.value);
    };

    const onChangeBranchDescriptionTextFieldValue = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
        data: { value: string }
    ) => {
        setBranchDescription(data.value);
    };

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            let CurrentValues: ContractBranch | NewContractBranch;
            if (props.Branch) {
                CurrentValues = {
                    id: props.Branch.id,
                    contractId: props.ContractId,
                    contractAddendumId: props.ContractAddendumId,
                    branchName: branchName!,
                    branchDescription: branchDescription!,
                    protocolIdRelatedToBranch: selectedProtocols?.[0]?.id
                };
                props.UpdateContractBranch(CurrentValues as ContractBranch);
            } else {
                CurrentValues = {
                    contractId: props.ContractId,
                    contractAddendumId: props.ContractAddendumId,
                    branchName: branchName!,
                    branchDescription: branchDescription!,
                    protocolIdRelatedToBranch: selectedProtocols?.[0]?.id
                };
                props.CreateContractBranch(CurrentValues as NewContractBranch);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    // footer
    const onRenderFooterContent = () => {
        return (
            <DrawerFooter>
                <Button onClick={HandleCancelForm} appearance="secondary">
                    <Trans i18nKey={'branchForm.footer.cancelButton'} />
                </Button>
                <Button
                    onClick={HandleSaveForm}
                    appearance="primary"
                    disabled={props.isAddingNewOne || props.Branch?.isUpdating}
                >
                    {props.isAddingNewOne || props.Branch?.isUpdating ? <><Spinner size="extra-tiny" />&nbsp;<span><Trans i18nKey={'branchForm.footer.saving'} /></span></> : <Trans i18nKey={'branchForm.footer.saveButton'} />}
                </Button>
            </DrawerFooter>
        );
    };

    const formFields = (
        <>
            <Field
                label={t('branchForm.formFields.branchName')}
                required
                validationMessage={
                    submittedForm && !branchName
                        ? t('branchForm.formFields.branchNameRequired')
                        : ''
                }
            >
                <Input
                    value={branchName || ''}
                    onChange={onChangeBranchNameTextFieldValue}
                />
            </Field>
            <Field
                label={t('branchForm.formFields.branchDescription')}
            >
                <Textarea
                    value={branchDescription || ''}
                    onChange={onChangeBranchDescriptionTextFieldValue}
                />
            </Field>
            <Field
                label={t('branchForm.formFields.protocolRelatedToBranch', 'Protocolo relacionado')}
            >
                <ProtocolSelectorComponent
                    selectionLimit={1}
                    selectedProtocols={selectedProtocols}
                    onChangeSelection={(protocolsSelected: Protocol[] | undefined) => setSelectedProtocols(protocolsSelected || [])}
                />
            </Field>
        </>
    );

    // form validation

    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (!branchName || branchName.trim() === '') {
            FormValidation.errors.push(t('branchForm.formFields.branchNameRequired'));
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    return (
        <Drawer
            open={props.isOpen}
            onOpenChange={(_, { open }) => !open && props.dismissPanel()}
            position="end"
            size="medium"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            onClick={props.dismissPanel}
                        />
                    }
                >
                    {t('branchForm.headerText.branchInformation')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {submittedForm && !FormValidationStatus?.success && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>{t('branchForm.messageBar.reviewInformation')}</MessageBarTitle>
                            <ul>
                                {FormValidationStatus?.errors.map(
                                    (Error: string, index: number) => (
                                        <li key={index}>{Error}</li>
                                    )
                                )}
                            </ul>
                        </MessageBarBody>
                    </MessageBar>
                )}
                {props.failOnAdding &&
                    props.error && (
                        <ErrorMessageComponent Error={props.error} />
                    )}
                {formFields}
            </DrawerBody>
            {onRenderFooterContent()}
        </Drawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(BranchFormComponent as any);